import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Brand from "../components/Brand";
import { Gift } from "lucide-react";

const Signup = () => {
    const { signup } = useAuth();
    const [sp] = useSearchParams();
    const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", referral_code: "" });
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const ref = sp.get("ref");
        if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
    }, [sp]);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (form.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }
        try {
            setSubmitting(true);
            const payload = {
                full_name: form.full_name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone.trim(),
                password: form.password,
            };
            if (form.referral_code.trim()) payload.referral_code = form.referral_code.trim().toUpperCase();
            await signup(payload);
            if (payload.referral_code) {
                toast.success(`Welcome to PILLS — ₹100 wallet credit added!`);
            } else {
                toast.success("Welcome to PILLS");
            }
            navigate("/", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message || "Signup failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-white px-6 py-16" data-testid="signup-page">
            <div className="absolute inset-0 -z-10 bg-brand-radial" aria-hidden />
            <div className="w-full max-w-md">
                <div className="mb-12 flex justify-center">
                    <Brand size="lg" tagline="AI-powered healthcare commerce" testId="signup-brand" />
                </div>
                <div className="glass-card rounded-3xl p-8 shadow-brand">
                    <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Create your PILLS account</h1>
                    <p className="mt-1 text-sm text-zinc-500">Premium healthcare delivered to your door.</p>

                    <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="signup-form">
                        <div>
                            <label className="label-uppercase">Full name</label>
                            <input
                                type="text"
                                name="full_name"
                                required
                                value={form.full_name}
                                onChange={onChange}
                                placeholder="Aarav Sharma"
                                className="input-clean mt-2"
                                data-testid="signup-name-input"
                                autoComplete="name"
                            />
                        </div>
                        <div>
                            <label className="label-uppercase">Email</label>
                            <input
                                type="email"
                                name="email"
                                required
                                value={form.email}
                                onChange={onChange}
                                placeholder="you@example.com"
                                className="input-clean mt-2"
                                data-testid="signup-email-input"
                                autoComplete="email"
                            />
                        </div>
                        <div>
                            <label className="label-uppercase">Phone</label>
                            <input
                                type="tel"
                                name="phone"
                                required
                                value={form.phone}
                                onChange={onChange}
                                placeholder="9876543210"
                                className="input-clean mt-2"
                                data-testid="signup-phone-input"
                                autoComplete="tel"
                            />
                        </div>
                        <div>
                            <label className="label-uppercase">Password</label>
                            <input
                                type="password"
                                name="password"
                                required
                                minLength={8}
                                value={form.password}
                                onChange={onChange}
                                placeholder="At least 8 characters"
                                className="input-clean mt-2"
                                data-testid="signup-password-input"
                                autoComplete="new-password"
                            />
                        </div>
                        <div>
                            <label className="label-uppercase flex items-center gap-1.5"><Gift className="h-3 w-3" /> Referral code <span className="lowercase text-zinc-400">(optional)</span></label>
                            <input
                                type="text"
                                name="referral_code"
                                value={form.referral_code}
                                onChange={(e) => setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase() }))}
                                placeholder="PILLS-XXXXXX"
                                className="input-clean mt-2 font-mono uppercase tracking-wider"
                                data-testid="signup-referral-input"
                                autoComplete="off"
                            />
                            {form.referral_code && <p className="mt-1.5 text-xs text-emerald-700">Get ₹100 wallet credit when you sign up.</p>}
                        </div>
                        <button type="submit" disabled={submitting} className="btn-brand w-full" data-testid="signup-submit-btn">
                            {submitting ? "Creating account..." : "Create account"}
                        </button>
                    </form>

                    <p className="mt-6 text-center text-sm text-zinc-500">
                        Already have an account?{" "}
                        <Link to="/login" className="font-medium text-brand-700 hover:underline" data-testid="signup-login-link">
                            Sign in
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Signup;
